import { useCallback, useEffect, useMemo, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import ReactApexChart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import {
  getScoreDeterminations,
  getScoreValues,
  getReferenceHighlights,
  type ScoreItem,
  type ScoreValue,
  type ReferenceHighlight,
} from '../../services/PatientScoreHistoryController';
import './HistoricalValuesPanel.scss';

interface HistoricalValuesPanelProps {
  patientId: number;
}

export default function HistoricalValuesPanel({ patientId }: HistoricalValuesPanelProps) {
  const [collapsed, setCollapsed] = useState(true);
  const [scores, setScores] = useState<ScoreItem[]>([]);
  const [filter, setFilter] = useState('');
  const [selectedScore, setSelectedScore] = useState<ScoreItem | null>(null);
  const [values, setValues] = useState<ScoreValue[]>([]);
  const [highlights, setHighlights] = useState<ReferenceHighlight[]>([]);
  const [loadingScores, setLoadingScores] = useState(true);
  const [loadingValues, setLoadingValues] = useState(false);

  // Load scores on mount
  useEffect(() => {
    setLoadingScores(true);
    getScoreDeterminations(patientId)
      .then(setScores)
      .catch(() => setScores([]))
      .finally(() => setLoadingScores(false));
  }, [patientId]);

  // Load values when score is selected
  useEffect(() => {
    if (!selectedScore) {
      setValues([]);
      setHighlights([]);
      return;
    }
    setLoadingValues(true);
    Promise.all([
      getScoreValues(patientId, selectedScore.scoreId, selectedScore.determinationId),
      selectedScore.numeric
        ? getReferenceHighlights(patientId, selectedScore.scoreId)
        : Promise.resolve([]),
    ])
      .then(([vals, refs]) => {
        setValues(vals);
        setHighlights(refs);
      })
      .catch(() => {
        setValues([]);
        setHighlights([]);
      })
      .finally(() => setLoadingValues(false));
  }, [patientId, selectedScore]);

  const filteredScores = useMemo(() => {
    if (!filter.trim()) return scores;
    const lower = filter.toLowerCase();
    return scores.filter((s) => s.description?.toLowerCase().includes(lower));
  }, [scores, filter]);

  const handleScoreClick = useCallback((score: ScoreItem) => {
    setSelectedScore((prev) =>
      prev?.scoreId === score.scoreId && prev?.determinationId === score.determinationId
        ? null
        : score,
    );
  }, []);

  // Chart data for numeric scores
  const chartOptions = useMemo<ApexOptions | null>(() => {
    if (!selectedScore?.numeric || values.length === 0) return null;
    const reversed = [...values].reverse();
    const categories = reversed.map((v) => v.date ?? '');
    const data = reversed.map((v) => v.value ?? 0);

    const annotations: ApexOptions['annotations'] = { yaxis: [] };
    for (const hl of highlights) {
      if (hl.drawLineMin && hl.minValue != null) {
        annotations.yaxis!.push({
          y: hl.minValue,
          borderColor: hl.color || '#ef4444',
          strokeDashArray: 4,
          label: { text: `Min: ${hl.minValue}`, style: { fontSize: '10px', color: '#fff', background: hl.color || '#ef4444' } },
        });
      }
      if (hl.drawLineMax && hl.maxValue != null) {
        annotations.yaxis!.push({
          y: hl.maxValue,
          borderColor: hl.color || '#ef4444',
          strokeDashArray: 4,
          label: { text: `Max: ${hl.maxValue}`, style: { fontSize: '10px', color: '#fff', background: hl.color || '#ef4444' } },
        });
      }
    }

    return {
      chart: { type: 'line', height: 200, toolbar: { show: false }, zoom: { enabled: false } },
      stroke: { curve: 'smooth', width: 2 },
      colors: ['#3CB8BA'],
      markers: { size: 4, colors: ['#01717C'], strokeColors: '#fff', strokeWidth: 2 },
      xaxis: {
        categories,
        labels: { rotate: -45, style: { fontSize: '9px' }, formatter: (val: string) => val ? val.substring(5, 10) : '' },
      },
      yaxis: {
        title: { text: selectedScore.unit ?? '', style: { fontSize: '10px' } },
        labels: { style: { fontSize: '10px' } },
      },
      annotations,
      tooltip: { x: { show: true }, y: { formatter: (val: number) => `${val} ${selectedScore.unit ?? ''}` } },
      grid: { borderColor: '#e2e8f0', strokeDashArray: 3 },
    };
  }, [selectedScore, values, highlights]);

  const chartSeries = useMemo(() => {
    if (!selectedScore?.numeric || values.length === 0) return [];
    const reversed = [...values].reverse();
    return [{ name: selectedScore.description, data: reversed.map((v) => v.value ?? 0) }];
  }, [selectedScore, values]);

  const dateTemplate = (row: ScoreValue) => (
    <span style={{ fontSize: '0.75rem' }}>{row.date ? row.date.substring(0, 10) : ''}</span>
  );

  const valueTemplate = (row: ScoreValue) => (
    <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>
      {row.value != null ? row.value : row.observation ?? ''}
    </span>
  );

  if (collapsed) {
    return (
      <aside className="historical-values-panel historical-values-panel--collapsed">
        <button
          type="button"
          className="historical-values-panel__expand-tab"
          onClick={() => setCollapsed(false)}
          title="Mostrar valores históricos"
        >
          <i className="pi pi-angle-left" aria-hidden="true" />
          <span className="historical-values-panel__expand-label">Valores históricos</span>
        </button>
      </aside>
    );
  }

  return (
    <aside className="historical-values-panel">
      <h3 className="historical-values-panel__title">
        <i className="pi pi-chart-line" />
        Valores históricos
        <button
          type="button"
          className="historical-values-panel__collapse-btn"
          onClick={() => setCollapsed(true)}
          title="Ocultar"
        >
          <i className="pi pi-angle-right" />
        </button>
      </h3>

      {/* Search */}
      <div className="historical-values-panel__search">
        <span className="p-input-icon-left" style={{ width: '100%' }}>
          <i className="pi pi-search" />
          <InputText
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Buscar score..."
            className="p-inputtext-sm"
            style={{ width: '100%' }}
          />
        </span>
      </div>

      {/* Score list */}
      {loadingScores ? (
        <div className="historical-values-panel__loading">
          <ProgressSpinner style={{ width: '24px', height: '24px' }} />
        </div>
      ) : (
        <ul className="historical-values-panel__score-list">
          {filteredScores.map((score) => {
            const isSelected =
              selectedScore?.scoreId === score.scoreId &&
              selectedScore?.determinationId === score.determinationId;
            return (
              <li
                key={`${score.scoreId}-${score.determinationId ?? 0}`}
                className={`historical-values-panel__score-item${isSelected ? ' historical-values-panel__score-item--selected' : ''}`}
                onClick={() => handleScoreClick(score)}
              >
                <span className="historical-values-panel__score-name">{score.description}</span>
                {score.unit ? (
                  <span className="historical-values-panel__score-unit">{score.unit}</span>
                ) : null}
              </li>
            );
          })}
          {filteredScores.length === 0 ? (
            <li className="historical-values-panel__empty">Sin resultados</li>
          ) : null}
        </ul>
      )}

      {/* Selected score detail */}
      {selectedScore ? (
        <div className="historical-values-panel__detail">
          <h4 className="historical-values-panel__detail-title">{selectedScore.description}</h4>

          {loadingValues ? (
            <div className="historical-values-panel__loading">
              <ProgressSpinner style={{ width: '24px', height: '24px' }} />
            </div>
          ) : (
            <>
              {/* Values table */}
              <DataTable
                value={values}
                size="small"
                scrollable
                scrollHeight="180px"
                emptyMessage="Sin registros"
                className="historical-values-panel__table"
              >
                <Column body={dateTemplate} header="Fecha" style={{ width: '90px' }} />
                <Column body={valueTemplate} header="Valor" />
              </DataTable>

              {/* Chart (only for numeric scores) */}
              {chartOptions && chartSeries.length > 0 ? (
                <div className="historical-values-panel__chart">
                  <ReactApexChart
                    options={chartOptions}
                    series={chartSeries}
                    type="line"
                    height={200}
                  />
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </aside>
  );
}
